import React, { useState } from 'react';

import { ActionIcon, Box, Button, Group, InputBase, NumberInput, Popover, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

type PercentageRangePickerProps = {
  className?: string;
  value: [number, number] | null;
  onChange: (value: [number, number] | null) => void;
  label?: string;
  placeholder?: string;
};

const formatInput = (value: [number, number] | null): string => {
  if (!value) return '';
  return `${value[0]}% - ${value[1]}%`;
};

const isValidPercentage = (percentage: number | ''): boolean => {
  return percentage !== '' && percentage >= 0;
};

const isPercentageRangeValid = (fromPercentage: number | '', toPercentage: number | ''): boolean => {
  if (!isValidPercentage(fromPercentage) || !isValidPercentage(toPercentage)) return false;
  return (fromPercentage as number) < (toPercentage as number);
};

const PercentageRangePicker = ({
  className,
  value,
  onChange,
  label,
  placeholder = 'Select percentage range',
}: PercentageRangePickerProps): JSX.Element => {
  const [open, setOpened] = useState(false);
  const [draft, setDraft] = useState<{
    from: number | '';
    to: number | '';
  }>({
    from: value?.[0] ?? '',
    to: value?.[1] ?? '',
  });
  const [error, setError] = useState('');

  const formatted = formatInput(value);
  const hasValue = value !== null;

  const handleUpdate = (): void => {
    const { from, to } = draft;

    if (from === '' || to === '') {
      setError('Both start and end percentages are required');
      return;
    }

    if (!isValidPercentage(from) || !isValidPercentage(to)) {
      setError('Please enter valid percentages (0.0 or greater)');
      return;
    }

    if (!isPercentageRangeValid(from, to)) {
      setError('Start percentage must be less than end percentage');
      return;
    }

    setError('');
    onChange([from as number, to as number]);
    setOpened(false);
  };

  const handlePercentageChange = (field: 'from' | 'to', newPercentage: number | ''): void => {
    setDraft(prev => ({
      ...prev,
      [field]: newPercentage,
    }));
    setError('');
  };

  const handleClear = (): void => {
    setDraft({ from: '', to: '' });
    onChange(null);
    setError('');
    setOpened(false);
  };

  const handleOpen = (): void => {
    setDraft({
      from: value?.[0] ?? '',
      to: value?.[1] ?? '',
    });
    setError('');
    setOpened(true);
  };

  const clearButton = hasValue ? (
    <ActionIcon onClick={handleClear}>
      <IconX size={14} />
    </ActionIcon>
  ) : null;

  return (
    <Popover opened={open} onClose={() => setOpened(false)} withArrow>
      <Popover.Target>
        <InputBase
          className={className}
          label={label}
          value={formatted}
          placeholder={placeholder}
          readOnly
          onClick={handleOpen}
          rightSection={clearButton}
        />
      </Popover.Target>
      <Popover.Dropdown style={{ maxWidth: '100%' }}>
        <Box>
          <Group
            align='flex-start'
            sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}
            mb='md'
          >
            <NumberInput
              label='From (%)'
              placeholder='0.0'
              value={draft.from}
              onChange={v => handlePercentageChange('from', v)}
              min={0}
              precision={1}
              step={1}
              error={error && (draft.from === '' || !isValidPercentage(draft.from))}
              styles={{ input: { width: '120px' } }}
            />
            <NumberInput
              label='To (%)'
              placeholder='100.0'
              value={draft.to}
              onChange={v => handlePercentageChange('to', v)}
              min={0}
              precision={1}
              step={1}
              error={error && (draft.to === '' || !isValidPercentage(draft.to))}
              styles={{ input: { width: '120px' } }}
            />
          </Group>
          {error && (
            <Text color='red' size='sm' mb='md'>
              {error}
            </Text>
          )}
          <Group sx={{ display: 'flex', justifyContent: 'flex-end' }} mt='md'>
            <Button variant='subtle' onClick={() => setOpened(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </Group>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default PercentageRangePicker;
